import { Component } from 'san';
import Icon from '../icon/index.js';
import Button from '../button/index.js';
import boxx from '@baidu/boxx';

var ToastPosition;
(function (ToastPosition) {
    ToastPosition["Top"] = "top";
    ToastPosition["Middle"] = "middle";
    ToastPosition["Bottom"] = "bottom";
})(ToastPosition || (ToastPosition = {}));
var ToastType;
(function (ToastType) {
    // Mobile 和 PC 端均支持的枚举类型
    ToastType["Success"] = "success";
    // Mobile 端枚举类型
    ToastType["Avatar"] = "avatar";
    ToastType["Refresh"] = "refresh";
    // PC 端枚举类型
    ToastType["Info"] = "info";
    ToastType["Error"] = "error";
    ToastType["Warning"] = "warning";
})(ToastType || (ToastType = {}));
var ToastSize;
(function (ToastSize) {
    ToastSize["MD"] = "md";
    ToastSize["LG"] = "lg";
})(ToastSize || (ToastSize = {}));
var ToastActionType;
(function (ToastActionType) {
    ToastActionType["Link"] = "link";
    ToastActionType["Button"] = "button";
})(ToastActionType || (ToastActionType = {}));

// Mobile 端默认 Toast 配置
const defaultConfig = {
    /**
     * 消息文本
     * @default ''
     */
    message: '',
    /**
     * Toast 类型
     * @default undefined
     */
    type: undefined,
    /**
     * Toast 大小
     * @default ToastSize.MD - 默认值为中等大小
     */
    size: ToastSize.MD,
    /**
     * 是否显示关闭按钮
     * @default false
     */
    closable: false,
    /**
     * Toast 持续显示时间
     * @default 2000 - 默认值为 2000 毫秒
     */
    duration: 2000,
    /**
     * Toast 显示位置
     * @default ToastPosition.Middle - 默认值为屏幕中央
     */
    position: ToastPosition.Middle,
    /**
     * Toast 上的 action 区域文本
     * @default undefined
     */
    actionText: undefined,
    /**
     * Toast 上的操作类型
     * @default ToastActionType.Link - 默认值为链接风格
     */
    actionType: ToastActionType.Link,
    /**
     * 图标的旋转一周的时间
     * @default 0 - 默认值不旋转
     */
    animationDuration: 0,
    /**
     * 是否允许使用原生 Toast
     * @default true
     */
    allowNative: true
};
// NaToast 类型枚举，决定样式和位置
var NaToastType;
(function (NaToastType) {
    // 纯文字，居中
    NaToastType["textCenter"] = "1";
    // 上图下文，居中
    NaToastType["pictureCenter"] = "2";
    // 底部跳转样式，（左 图标[可选]+title文本[可选]+message文本[必选]，右跳转按钮，底部显示
    NaToastType["jumpBottom"] = "3";
    // 底部功能选择样式：icon左侧图标[必选]+message左侧文本[必选]+ButtonText文本+ clickCallback，底部显示
    NaToastType["clickBottom"] = "4";
})(NaToastType || (NaToastType = {}));
// NaToast 跳转按钮风格
var NaToastActionStyle;
(function (NaToastActionStyle) {
    // 文案与跳转按钮间存在分割线且跳转按钮中存在按钮箭头的样式
    NaToastActionStyle["link"] = "1";
    // 无分割线，跳转按钮为蓝底白字样式的Toast
    NaToastActionStyle["button"] = "2";
})(NaToastActionStyle || (NaToastActionStyle = {}));
// 组件支持的图标对应的 NaToast 图标 url
const NaToastIcon = {
    [ToastType.Success]: 'https://gips2.baidu.com/it/u=3218868066,525031659&fm=3028&app=3028&f=PNG&fmt=auto&q=75&size=f54_54',
    [ToastType.Refresh]: 'https://gips1.baidu.com/it/u=1849808201,2412393131&fm=3028&app=3028&f=PNG&fmt=auto&q=75&size=f54_54'
};

/**
 * @file Toast
 * @author mengwenqiao@baidu.com
 */
const hasNaToast = boxx && boxx.canIUse('commonUI.showToast');
class ToastComponent extends Component {
    /**
     * 初始化数据
     */
    initData() {
        return Object.assign({
            // template variables
            ToastSize,
            ToastType,
            ToastActionType,
            // data
            visible: false
        }, defaultConfig);
    }
    /**
     * 展示 Toast
     */
    show() {
        this.data.set('visible', true);
    }
    /**
     * 隐藏 Toast
     */
    hide() {
        this.data.set('visible', false);
    }
    /**
     * 点击 Toast action 区域回调函数
     * @param event Event 对象
     */
    onAction(event) {
        this.fire('action', { event });
        this.hide();
    }
}
ToastComponent.template = `
        <div
            s-if="visible"
            class="cos-toast{{position ? ' cos-toast-' + position : ''}}">
            <div class="cos-toast-container{{size ? ' cos-toast-' + size : ''}}">
                <cos-icon
                    s-if="size === ToastSize.LG && type"
                    name="{{iconName}}"
                    class="cos-toast-lg-icon"
                    style="{{ {'animation-duration': animationDuration + 'ms'} }}"
                />

                <div class="cos-toast-content">
                    <cos-icon
                        s-if="size === ToastSize.MD && type"
                        class="cos-toast-md-icon"
                        style="{{ {'animation-duration': animationDuration + 'ms'} }}"
                        name="{{iconName}}"
                    />
                    <!--消除段落间距-->
                    <div class="cos-text-body-lg cos-space-m-none cos-toast-message">
                        {{displayMessage}}
                    </div>
                    <cos-button
                        s-if="actionText && actionType === ToastActionType.Button"
                        class="cos-toast-button"
                        size="sm"
                        on-click="onAction"
                    >
                        {{displayActionText}}
                    </cos-button>
                    <div
                        s-elif="actionText && actionType === ToastActionType.Link"
                        class="cos-toast-link"
                        on-click="onAction"
                    >
                        <div class="cos-toast-link-divider" />
                        <div class="cos-toast-link-text">
                            {{displayActionText}}
                        </div>
                        <cos-icon name="right" />
                    </div>
                </div>
            </div>
        </div>
    `;
ToastComponent.components = {
    'cos-button': Button,
    'cos-icon': Icon
};
ToastComponent.computed = {
    /**
     * 获取图标名称。
     * @param this ToastComponent 实例对象
     * @returns 返回图标名称，如果类型为 Success，返回 "check-circle"；否则返回空字符串，后续需要扩展
     */
    iconName() {
        switch (this.data.get('type')) {
            case ToastType.Success: {
                return 'check-circle';
            }
            case ToastType.Refresh: {
                return 'refresh';
            }
            default: {
                return '';
            }
        }
    },
    /**
     * 根据当前 Toast 组件的消息数据进行处理，返回处理后的字符串
     * @param this - 当前 Toast 组件对象
     * @returns 返回处理后的字符串
     */
    displayMessage() {
        let message = this.data.get('message');
        switch (this.data.get('size')) {
            case ToastSize.MD: {
                const maxLength = 10;
                if (this.data.get('type') && message.length > maxLength) {
                    message = message.slice(0, maxLength);
                }
                break;
            }
            case ToastSize.LG: {
                const maxLength = 6;
                if (message.length > maxLength) {
                    message = message.slice(0, maxLength);
                }
                break;
            }
        }
        return message;
    },
    /**
     * 获取 Toast 组件的操作文本。
     * @param this Toast 组件实例。
     * @returns 如果操作文本存在且长度大于 4 个字符，则返回截取前 4 个字符；否则返回 undefined。
     */
    displayActionText() {
        let actionText = this.data.get('actionText');
        if (actionText && actionText.length > 4) {
            actionText = actionText.slice(0, 4);
        }
        return actionText;
    }
};
/**
 * 判断配置 NaToast 是否支持，下列场景无法支持
 * 1. 尺寸为 LG 类型且 type 不为 Success
 * 2. 位置在上面
 * 3. 有图标或按钮且位置不在底部
 * 4. 纯文本且位置不在中间
 * 5. 添加动态图标
 *
 * @param config Toast 配置信息
 * @returns 是否支持显示 NaToast 通知
 */
function useNaToast(config) {
    if (config.size === ToastSize.LG && config.type !== ToastType.Success) {
        return false;
    }
    const hasIcon = !!config.type;
    const hasButton = !!config.actionText;
    if (config.position === ToastPosition.Top) {
        return false;
    }
    if ((hasIcon || hasButton) && config.position !== ToastPosition.Bottom) {
        return false;
    }
    if (!hasIcon && !hasButton && config.position !== ToastPosition.Middle) {
        return false;
    }
    if (config.animationDuration) {
        return false;
    }
    return true;
}
/**
 * 将 ToastConfig 配置转换为 NaToastParams 参数对象
 *
 * @param config ToastConfig 配置
 */
function config2NaToastParams(config) {
    var _a;
    const params = {
        // 默认为纯文本，如果有图标或按钮则修改类型
        type: NaToastType.textCenter,
        message: config.message,
        time: String(((_a = config.duration) !== null && _a !== void 0 ? _a : defaultConfig.duration) / 1000)
    };
    // 转换图标属性，当前仅支持 refresh 和 success
    if (config.type) {
        if (config.type === ToastType.Refresh || config.type === ToastType.Success) {
            params.type = NaToastType.clickBottom;
            params.icon = NaToastIcon[config.type];
        }
    }
    // 上图下文只支持 success
    if (config.type === ToastType.Success && config.size === ToastSize.LG) {
        params.type = NaToastType.pictureCenter;
        params.image = 'success';
    }
    // 转换按钮相关属性
    if (config.actionText) {
        params.type = NaToastType.clickBottom;
        params.buttonText = config.actionText;
        params.style = config.actionType === 'link' ? NaToastActionStyle.link : NaToastActionStyle.button;
        params.clickCallback = config.onAction;
    }
    return params;
}
/**
 * 调用端能力Toast
 *
 * @param config 配置对象，包含通知提示的各种属性
 */
function showNaToast(config) {
    try {
        const params = config2NaToastParams(config);
        boxx.commonUI.showToast({
            ...params,
            fail: () => {
                throw new Error('调用 Toast 失败');
            }
        });
    }
    catch (error) {
        throw new Error('调用 Toast 失败');
    }
}
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class Toast {
    /**
     * 初始化 Toast 组件
     *
     * @param config - 传入的配置对象
     */
    static init(config) {
        if (Toast.component) {
            Toast.component.hide();
        }
        Toast.config = config;
        Toast.component = new ToastComponent({ data: Toast.config });
        if (Toast.config.onAction) {
            Toast.component.on('action', Toast.config.onAction);
        }
        // 处理当前 Toast 实例挂载到 DOM 哪个节点上
        const parentNode = config.parentSelector || document.body;
        Toast.component.attach(parentNode);
    }
    static show(config) {
        const newConfig = Object.assign({}, defaultConfig, config);
        if (newConfig.allowNative && hasNaToast && useNaToast(newConfig)) {
            try {
                Toast.hide();
                showNaToast(newConfig);
            }
            catch (error) {
                Toast.showH5Toast(newConfig);
            }
        }
        else {
            Toast.showH5Toast(newConfig);
        }
    }
    static showH5Toast(config) {
        Toast.init(config);
        Toast.component.show();
        if (Toast.hideTimer) {
            clearTimeout(Toast.hideTimer);
            Toast.hideTimer = null;
        }
        Toast.hideTimer = setTimeout(() => {
            Toast.component.hide();
        }, Toast.config.duration);
    }
    /**
     * 关闭 Toast
     */
    static hide() {
        Toast.component && Toast.component.hide();
    }
}
Toast.hideTimer = null;

export { Toast as default };
